import { CognitoAuthOptions } from 'amazon-cognito-auth-js'

type FeatureFlags = Record<Features, boolean>

export enum Features {
    FAST_TRACK_APPROVAL = 'FAST_TRACK_APPROVAL',
    LLM_MIXTRAL_CRA_SCORING = 'LLM_MIXTRAL_CRA_SCORING',
    MIXTRAL_ENHANCED_CRA_SCORING = 'MIXTRAL_ENHANCED_CRA_SCORING',
    GHS_AUTHORING_ASSISTANT = 'GHS_AUTHORING_ASSISTANT',
    SYNC_DRAFT_DOWNSTREAM = 'SYNC_DRAFT_DOWNSTREAM',
}

export const ALL_FEATURE_FLAGS_DISABLED: FeatureFlags = Object.freeze(
    Object.values(Features).reduce((o, k) => ({ ...o, [k]: true }), {})
) as never as FeatureFlags

export interface AppConfig {
    stage: STAGE
    auth: CognitoAuthOptions
    identityPoolId: string
    s3MediaBucketAccessPointARN: string
    // API V2
    backendAPIBaseUrl: string
    // API V1 (old)
    backendAPIV1BaseUrl: string
    deliveryPreviewBaseUrl: string
    aptUrl: string
    approvalUrl: string
    isItDownDashboardName: string
    authoringAssistantAPIBaseUrl: string

    notificationServiceBaseUrl: string

    //Scoring Test Project
    wbsConfig: {
        s3InputBucketName: string
        wbsAPIUrl: string
        wbsIdentityPoolId: string
    }
    wbsAuth: CognitoAuthOptions
    featureFlags?: FeatureFlags
}

export enum STAGE {
    BETA = 'beta',
    GAMMA = 'gamma',
    PROD = 'prod',
    LOCAL = 'local',
}

export const QA_AUTOMATION_STS_ASSUME_ROLE =
    'arn:aws:iam::382110919776:role/RxtHofAuthoringServiceRole'

export const CONFIGS_BY_ENVIRONMENT: Record<STAGE, AppConfig> = {
    local: {
        // Designer app
        stage: STAGE.LOCAL,
        auth: {
            AppWebDomain: 'assessment-designer-api-beta.auth.us-west-2.amazoncognito.com',
            ClientId: '5cs0lm7551h8gmcoj2knuael37',
            RedirectUriSignIn:
                window.location.origin === 'http://0.0.0.0:3000'
                    ? 'http://localhost:3000'
                    : window.location.origin,
            RedirectUriSignOut:
                window.location.origin === 'http://0.0.0.0:3000'
                    ? 'http://localhost:3000'
                    : window.location.origin,
            TokenScopesArray: [],
            UserPoolId: 'us-west-2_ty6POd3dn',
        },
        identityPoolId: 'us-west-2:f9f2c16f-b417-41d5-9c35-e7b06710546b',
        s3MediaBucketAccessPointARN:
            'arn:aws:s3:us-west-2:335240694869:accesspoint/media-bucket-access-point-beta',
        backendAPIBaseUrl:
            'https://api.beta.workbench.assessments.recruiting.people-tech.a2z.com/v2',
        backendAPIV1BaseUrl:
            'https://api.beta.workbench.assessments.recruiting.people-tech.a2z.com',
        deliveryPreviewBaseUrl: 'https://delta.assessments.amazon.jobs',
        aptUrl: 'https://wmme50ds9g.execute-api.us-west-2.amazonaws.com/beta/generate',
        approvalUrl:
            'https://api.beta.workbench.assessments.recruiting.people-tech.a2z.com/approval-cognito',
        isItDownDashboardName: 'aa-designer-beta',
        authoringAssistantAPIBaseUrl: 'https://5rsgr6m4x0.execute-api.us-west-2.amazonaws.com/beta',
        notificationServiceBaseUrl:
            'https://ntfs.beta.workbench.assessments.recruiting.people-tech.a2z.com',
        wbsConfig: {
            s3InputBucketName: 'workflow-scoring-testing-beta',
            wbsAPIUrl: 'https://6t77jsiook.execute-api.us-west-2.amazonaws.com/beta',
            wbsIdentityPoolId: 'us-west-2:4517a79c-4501-408c-ae10-f0ffcf02b36e',
        },
        wbsAuth: {
            AppWebDomain: 'wbs-api-beta.auth.us-west-2.amazoncognito.com',
            ClientId: 's65oosg52jr1f8q4jbbq52112',
            RedirectUriSignIn: 'http://localhost:3000/scoring-test-v2',
            RedirectUriSignOut: 'http://localhost:3000/scoring-test-v2',
            TokenScopesArray: [
                'https://backend.wbs.recruiting.people-tech.a2z.com/scoring.testing',
                'https://backend.wbs.recruiting.people-tech.a2z.com/workflows.search',
                'https://backend.wbs.recruiting.people-tech.a2z.com/scoring.testing.v2',
                'https://backend.wbs.recruiting.people-tech.a2z.com/workflows.getAllVariationsScores',
                'https://backend.wbs.recruiting.people-tech.a2z.com/scoring.testing.validation',
                'openid',
            ],
            UserPoolId: 'us-west-2_jtJ79GIqR',
        },
        featureFlags: {
            [Features.FAST_TRACK_APPROVAL]: true,
            [Features.LLM_MIXTRAL_CRA_SCORING]: true,
            [Features.MIXTRAL_ENHANCED_CRA_SCORING]: true,
            [Features.GHS_AUTHORING_ASSISTANT]: true,
            [Features.SYNC_DRAFT_DOWNSTREAM]: true,
        },
    },
    beta: {
        stage: STAGE.BETA,
        auth: {
            AppWebDomain: 'assessment-designer-api-beta.auth.us-west-2.amazoncognito.com',
            ClientId: '5cs0lm7551h8gmcoj2knuael37',
            RedirectUriSignIn: window.location.origin,
            RedirectUriSignOut: window.location.origin,
            TokenScopesArray: [],
            UserPoolId: 'us-west-2_ty6POd3dn',
        },
        identityPoolId: 'us-west-2:f9f2c16f-b417-41d5-9c35-e7b06710546b',
        s3MediaBucketAccessPointARN:
            'arn:aws:s3:us-west-2:335240694869:accesspoint/media-bucket-access-point-beta',
        backendAPIBaseUrl:
            'https://api.beta.workbench.assessments.recruiting.people-tech.a2z.com/v2',
        backendAPIV1BaseUrl:
            'https://api.beta.workbench.assessments.recruiting.people-tech.a2z.com',
        deliveryPreviewBaseUrl: 'https://delta.assessments.amazon.jobs',
        aptUrl: 'https://wmme50ds9g.execute-api.us-west-2.amazonaws.com/beta/generate',
        approvalUrl:
            'https://api.beta.workbench.assessments.recruiting.people-tech.a2z.com/approval-cognito',
        isItDownDashboardName: 'aa-designer-beta',
        authoringAssistantAPIBaseUrl: 'https://5rsgr6m4x0.execute-api.us-west-2.amazonaws.com/beta',
        notificationServiceBaseUrl:
            'https://ntfs.beta.workbench.assessments.recruiting.people-tech.a2z.com',

        wbsConfig: {
            s3InputBucketName: 'workflow-scoring-testing-beta',
            wbsAPIUrl: 'https://6t77jsiook.execute-api.us-west-2.amazonaws.com/beta',
            wbsIdentityPoolId: 'us-west-2:4517a79c-4501-408c-ae10-f0ffcf02b36e',
        },
        wbsAuth: {
            AppWebDomain: 'wbs-api-beta.auth.us-west-2.amazoncognito.com',
            ClientId: 's65oosg52jr1f8q4jbbq52112',
            RedirectUriSignIn:
                'https://designer-beta.assessments.recruiting.people-tech.a2z.com/scoring-test-v2',
            RedirectUriSignOut:
                'https://designer-beta.assessments.recruiting.people-tech.a2z.com/scoring-test-v2',
            TokenScopesArray: [
                'https://backend.wbs.recruiting.people-tech.a2z.com/scoring.testing',
                'https://backend.wbs.recruiting.people-tech.a2z.com/workflows.search',
                'https://backend.wbs.recruiting.people-tech.a2z.com/scoring.testing.v2',
                'https://backend.wbs.recruiting.people-tech.a2z.com/workflows.getAllVariationsScores',
                'https://backend.wbs.recruiting.people-tech.a2z.com/scoring.testing.validation',
                'openid',
            ],
            UserPoolId: 'us-west-2_jtJ79GIqR',
        },
        featureFlags: {
            [Features.FAST_TRACK_APPROVAL]: true,
            [Features.LLM_MIXTRAL_CRA_SCORING]: true,
            [Features.MIXTRAL_ENHANCED_CRA_SCORING]: true,
            [Features.GHS_AUTHORING_ASSISTANT]: true,
            [Features.SYNC_DRAFT_DOWNSTREAM]: true,
        },
    },
    gamma: {
        stage: STAGE.GAMMA,
        auth: {
            AppWebDomain: 'assessment-designer-api-gamma.auth.us-west-2.amazoncognito.com',
            ClientId: '516t5jjuvm0k6svcvh0icj5m5p',
            RedirectUriSignIn: window.location.origin,
            RedirectUriSignOut: window.location.origin,
            TokenScopesArray: [],
            UserPoolId: 'us-west-2_X61gwICsr',
        },
        identityPoolId: 'us-west-2:edf8343d-0967-40fe-b193-5b66efaab98b',
        s3MediaBucketAccessPointARN:
            'arn:aws:s3:us-west-2:048964474750:accesspoint/media-bucket-access-point-gamma',
        backendAPIBaseUrl:
            'https://api.gamma.workbench.assessments.recruiting.people-tech.a2z.com/v2',
        backendAPIV1BaseUrl:
            'https://api.gamma.workbench.assessments.recruiting.people-tech.a2z.com',
        deliveryPreviewBaseUrl: 'https://delta.assessments.amazon.jobs',
        aptUrl: 'https://nlengv73ji.execute-api.us-west-2.amazonaws.com/gamma/generate',
        approvalUrl:
            'https://api.gamma.workbench.assessments.recruiting.people-tech.a2z.com/approval-cognito',
        isItDownDashboardName: 'aa-designer-gamma',
        authoringAssistantAPIBaseUrl:
            'https://8t2cc1l140.execute-api.us-west-2.amazonaws.com/gamma',
        notificationServiceBaseUrl:
            'https://ntfs.gamma.workbench.assessments.recruiting.people-tech.a2z.com',

        wbsConfig: {
            s3InputBucketName: 'workflow-scoring-testing-gamma',
            wbsAPIUrl: 'https://q9jhg5khwg.execute-api.us-west-2.amazonaws.com/gamma',
            wbsIdentityPoolId: 'us-west-2:a3992f2f-22f8-4b4d-b7d9-ee3215fd9a2c',
        },
        wbsAuth: {
            AppWebDomain: 'wbs-api-gamma.auth.us-west-2.amazoncognito.com',
            ClientId: '2dl4j6ikbl7p6ra99j1ju598e9',
            RedirectUriSignIn:
                'https://designer-gamma.assessments.recruiting.people-tech.a2z.com/scoring-test-v2',
            RedirectUriSignOut:
                'https://designer-gamma.assessments.recruiting.people-tech.a2z.com/scoring-test-v2',
            TokenScopesArray: [
                'https://backend.wbs.recruiting.people-tech.a2z.com/scoring.testing',
                'https://backend.wbs.recruiting.people-tech.a2z.com/workflows.search',
                'https://backend.wbs.recruiting.people-tech.a2z.com/scoring.testing.v2',
                'https://backend.wbs.recruiting.people-tech.a2z.com/workflows.getAllVariationsScores',
                'https://backend.wbs.recruiting.people-tech.a2z.com/scoring.testing.validation',
                'openid',
            ],
            UserPoolId: 'us-west-2_8wC9ngzOq',
        },
        featureFlags: {
            [Features.FAST_TRACK_APPROVAL]: true,
            [Features.LLM_MIXTRAL_CRA_SCORING]: true,
            [Features.MIXTRAL_ENHANCED_CRA_SCORING]: true,
            [Features.GHS_AUTHORING_ASSISTANT]: true,
            [Features.SYNC_DRAFT_DOWNSTREAM]: true,
        },
    },
    prod: {
        stage: STAGE.PROD,
        auth: {
            AppWebDomain: 'assessment-designer-api-prod.auth.us-west-2.amazoncognito.com',
            ClientId: '33nl9ea286uulkvhu25hlrj1md',
            RedirectUriSignIn: window.location.origin,
            RedirectUriSignOut: window.location.origin,
            TokenScopesArray: [],
            UserPoolId: 'us-west-2_5AV7M76kz',
        },
        identityPoolId: 'us-west-2:384076f6-2c61-40a3-bd7a-6737e7d0401f',
        s3MediaBucketAccessPointARN:
            'arn:aws:s3:us-west-2:993859354546:accesspoint/media-bucket-access-point-prod',
        backendAPIBaseUrl:
            'https://api.prod.workbench.assessments.recruiting.people-tech.a2z.com/v2',
        backendAPIV1BaseUrl:
            'https://api.prod.workbench.assessments.recruiting.people-tech.a2z.com',
        deliveryPreviewBaseUrl: 'https://delta.assessments.amazon.jobs',
        aptUrl: 'https://sjrkv8xnb5.execute-api.us-west-2.amazonaws.com/prod/generate',
        approvalUrl:
            'https://api.prod.workbench.assessments.recruiting.people-tech.a2z.com/approval-cognito',
        isItDownDashboardName: 'aa-designer',
        authoringAssistantAPIBaseUrl: ' ',
        notificationServiceBaseUrl:
            'https://ntfs.prod.workbench.assessments.recruiting.people-tech.a2z.com',

        wbsConfig: {
            s3InputBucketName: 'workflow-scoring-testing-prod',
            wbsAPIUrl: 'https://t9bin218xl.execute-api.us-west-2.amazonaws.com/prod',
            wbsIdentityPoolId: 'us-west-2:36ebef14-bd2f-43a6-afd4-8aabf81767e1',
        },
        wbsAuth: {
            AppWebDomain: 'wbs-api-prod.auth.us-west-2.amazoncognito.com',
            ClientId: '6tl2ab8l0ckjso94dek2pe6g7c',
            RedirectUriSignIn:
                'https://assessment-designer.assessments.recruiting.people-tech.a2z.com/scoring-test-v2',
            RedirectUriSignOut:
                'https://assessment-designer.assessments.recruiting.people-tech.a2z.com/scoring-test-v2',
            TokenScopesArray: [
                'https://backend.wbs.recruiting.people-tech.a2z.com/scoring.testing',
                'https://backend.wbs.recruiting.people-tech.a2z.com/workflows.search',
                'https://backend.wbs.recruiting.people-tech.a2z.com/scoring.testing.v2',
                'https://backend.wbs.recruiting.people-tech.a2z.com/workflows.getAllVariationsScores',
                'https://backend.wbs.recruiting.people-tech.a2z.com/scoring.testing.validation',
                'openid',
            ],
            UserPoolId: 'us-west-2_JFAP3BZlh',
        },
        featureFlags: {
            [Features.FAST_TRACK_APPROVAL]: true,
            [Features.LLM_MIXTRAL_CRA_SCORING]: true,
            [Features.MIXTRAL_ENHANCED_CRA_SCORING]: true,
            [Features.GHS_AUTHORING_ASSISTANT]: false,
            [Features.SYNC_DRAFT_DOWNSTREAM]: true,
        },
    },
}

const uriMap: Record<string, STAGE> = {
    'localhost:3000': STAGE.LOCAL,
    '0.0.0.0:3000': STAGE.LOCAL,
    'duiyv5gwzq9us.cloudfront.net': STAGE.BETA,
    'd1gxf9vr826jj.cloudfront.net': STAGE.GAMMA,
    'd33updycsknwr8.cloudfront.net': STAGE.PROD,
    'designer-beta.assessments.recruiting.people-tech.a2z.com': STAGE.BETA,
    'designer-gamma.assessments.recruiting.people-tech.a2z.com': STAGE.GAMMA,
    'assessment-designer.assessments.recruiting.people-tech.a2z.com': STAGE.PROD,
}

const workbenchUriMap: Record<string, STAGE> = {
    'alpha.app.workbench.assessments.recruiting.people-tech.a2z.com': STAGE.BETA,
    'beta.app.workbench.assessments.recruiting.people-tech.a2z.com': STAGE.BETA,
    'gamma.app.workbench.assessments.recruiting.people-tech.a2z.com': STAGE.GAMMA,
    'app.workbench.assessments.recruiting.people-tech.a2z.com': STAGE.PROD,

    'd53bdfhl38lom.cloudfront.net': STAGE.BETA,
    'dk8g42bv078ek.cloudfront.net': STAGE.BETA,
    'dznjy0ihn9f19.cloudfront.net': STAGE.GAMMA,
    'd39f1mgv7no92o.cloudfront.net': STAGE.PROD,
}

export const APP_CONFIGS: Record<string, AppConfig> = {}
for (const [uri, stage] of Object.entries(uriMap)) {
    APP_CONFIGS[uri] = CONFIGS_BY_ENVIRONMENT[stage]
}

for (const [uri, stage] of Object.entries(workbenchUriMap)) {
    const config = CONFIGS_BY_ENVIRONMENT[stage]
    APP_CONFIGS[uri] = {
        ...config,
        wbsAuth: {
            ...config.wbsAuth,
            RedirectUriSignIn: `https://${uri}/scoring-test-v2`,
            RedirectUriSignOut: `https://${uri}/scoring-test-v2`,
        },
    }
}

const CURRENT_CONFIG: AppConfig = APP_CONFIGS[window.location.host] || ({} as AppConfig)
export const APP_CONFIG: AppConfig = CURRENT_CONFIG

export const ON_TEST_ENVIRONMENT = () => {
    const host = window.location.host
    const environment: STAGE | undefined = uriMap[host] ?? workbenchUriMap[host]

    return [undefined, STAGE.LOCAL, STAGE.BETA, STAGE.GAMMA].includes(environment)
}

export const modulePreviewUrlForUAT =
    'https://delivery-gamma.assessments.recruiting.people-tech.a2z.com/#/internal/moduleTemplate'

const designerTeamAliases = [
    'amrgn',
    'dhirama',
    'imrada',
    'gsmakkar',
    'mtthammi',
    'trankn',
    'alexlego',
    'tkmin',
    'bnwong',
    'tommtang',
    'zyanzhen',
    'sulinski',
    'samkasp',
]

export function getReviewersList(currentUserName: string) {
    let reviewAliases = [
        'aimtripp',
        'adryster',
        'annehans',
        'arunvall',
        'blagad',
        'cantonik',
        'chrhonts',
        'creddock',
        'damlep',
        'dtomeh',
        'elisela',
        'flemkels',
        'gatemat',
        'gcdanna',
        'greenjp',
        'grieko',
        'jccapman',
        'jennharv',
        'jgaetano',
        'jgeimer',
        'jhoult',
        'josehare',
        'julcap',
        'juliesam',
        'kayosady',
        'kefa',
        'krbarr',
        'leemattj',
        'lwasko',
        'lwisjn',
        'markowmm',
        'msstoeck',
        'rbalyeat',
        'rbrusso',
        'rucpate',
        'rugakris',
        'shreay',
        'stemera',
        'suharini',
        'taferotr',
        'tcotter',
        'tlagan',
        'usshahid',
        'yashnqsh',
        'kogiba',
        'jthlms',
        'suyogd',
        'dtheil',
    ]

    if (designerTeamAliases.includes(currentUserName)) {
        reviewAliases = reviewAliases.concat(designerTeamAliases)
    }

    return reviewAliases.filter((value) => value != currentUserName).sort()
}

export const ADMIN_OVERRIDE_LIST = [
    ...designerTeamAliases,
    'markowmm',
    'krbarr',
    'edingtok',
    'josehare',
    'tmynear',
    'suharini',
    'msstoeck',
]
